/**
 * Interface representing validation rules for a form field.
 */
interface ValidationRules {
  name: string;
  value: string;
  isRequired?: boolean;
  isEmail?: boolean;
}

/**
 * Represents the result of a validation operation.
 */
interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validates a form based on the provided validation rules.
 *
 * @param {ValidationRules[]} fields - An array of validation rules for each field.
 * @returns {ValidationResult} An object containing the validation result and any errors.
 *
 * The validation rules for each field can include:
 * - `name` (string): The name of the field.
 * - `value` (string): The value of the field.
 * - `isRequired` (boolean): Whether the field is required.
 * - `isEmail` (boolean): Whether the field should be validated as an email.
 *
 * The validation result includes:
 * - `isValid` (boolean): Whether the form is valid.
 * - `errors` (Record<string, string>): An object containing error messages for each invalid field.
 *
 * Error messages are provided in German:
 * - "Dieses Feld ist erforderlich." for required fields that are empty.
 * - "Bitte geben Sie eine gültige E-Mail-Adresse ein." for fields that should contain a valid email address but do not.
 */
export const validateForm = (fields: ValidationRules[]): ValidationResult => {
  const errors: Record<string, string> = {};

  fields.forEach(({ name, value, isRequired, isEmail }) => {
    if (isRequired && !value.trim()) {
      errors[name] = "Dieses Feld ist erforderlich.";
    }

    if (isEmail && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errors[name] = "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};